import { Link } from 'react-router-dom';
import { 
  Target, 
  Heart, 
  Globe, 
  Users, 
  Lightbulb,
  Shield,
  Handshake,
  Leaf,
  ArrowRight,
  Quote
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const coreValues = [
  {
    icon: Shield,
    title: 'Integrity',
    description: 'We operate with complete transparency and honesty. Every engine description is accurate, every promise is kept, and every customer is treated with respect.',
    color: 'from-blue-500 to-blue-600'
  },
  {
    icon: Lightbulb,
    title: 'Excellence',
    description: 'We pursue excellence in everything we do - from engine quality and testing to customer service and support. Good enough is never enough.',
    color: 'from-orange-500 to-red-500'
  },
  {
    icon: Handshake,
    title: 'Partnership',
    description: 'We view every customer relationship as a long-term partnership. Your success is our success, and we&apos;re committed to supporting you beyond the sale.',
    color: 'from-green-500 to-emerald-600'
  },
  {
    icon: Globe,
    title: 'Global Reach',
    description: 'With customers on every continent, we understand diverse needs and deliver solutions worldwide while maintaining local expertise and cultural sensitivity.',
    color: 'from-purple-500 to-indigo-600'
  },
  {
    icon: Users,
    title: 'Team Spirit',
    description: 'Our diverse, multicultural team brings together expertise from around the world. We celebrate our differences and unite around our shared mission.',
    color: 'from-pink-500 to-rose-600'
  },
  {
    icon: Leaf,
    title: 'Sustainability',
    description: 'By extending the life of quality marine engines, we reduce waste and environmental impact. Reuse is the most powerful form of recycling.',
    color: 'from-teal-500 to-cyan-600'
  }
];

const stats = [
  { value: '15+', label: 'Years in Business' },
  { value: '50+', label: 'Countries Served' },
  { value: '5000+', label: 'Engines Sold' },
  { value: '98%', label: 'Customer Satisfaction' }
];

export default function PurposePage() {
  return (
    <main className="bg-slate-50">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 rounded-full text-orange-400 text-sm font-medium mb-6">
              <Heart className="w-4 h-4" />
              Why We Do What We Do
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Our Purpose &
              <span className="text-orange-500"> Values</span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
              At EngineShipParts, we&apos;re driven by a simple belief: every vessel deserves reliable power, 
              and every customer deserves exceptional service. This is what guides us every day.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-slate-50 to-orange-50 rounded-3xl p-12 border border-slate-200 relative overflow-hidden">
              <Quote className="absolute top-6 left-6 w-16 h-16 text-orange-200" />
              <Quote className="absolute bottom-6 right-6 w-16 h-16 text-orange-200 rotate-180" />
              
              <div className="relative z-10">
                <h2 className="text-2xl font-bold text-orange-500 mb-4 flex items-center gap-2">
                  <Target className="w-6 h-6" />
                  Our Mission
                </h2>
                <p className="text-2xl lg:text-3xl text-slate-800 font-medium leading-relaxed">
                  To be the world&apos;s most trusted source for quality marine engines, connecting 
                  buyers with reliable power solutions through expertise, integrity, and exceptional service.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-red-500">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-white/80">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              These principles guide every decision we make and every interaction we have.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreValues.map((value, index) => (
              <div 
                key={index}
                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100 hover:-translate-y-1"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${value.color} rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                <p className="text-slate-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="py-20 bg-slate-900">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Our Vision for the Future
            </h2>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              We envision a maritime industry where quality used engines are valued as highly as new ones, 
              where sustainability and performance go hand in hand, and where every vessel owner has access 
              to reliable, affordable power solutions backed by expert support.
            </p>
            <p className="text-lg text-slate-400 mb-12">
              From our headquarters in Qinhuangdao, China, we&apos;re building bridges across the global 
              maritime community, bringing together buyers and sellers with trust and transparency.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/about/team">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3">
                  Meet Our Team
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/inventory">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-3">
                  Explore Inventory
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-12 lg:p-16 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Work With Us?
            </h2>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              Whether you&apos;re looking to buy or sell marine engines, we&apos;d love to hear from you.
            </p>
            <Link to="/contact">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 text-lg">
                Get in Touch
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
